import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Row, Col, Card } from 'react-bootstrap';

import { getFile } from '../../../../util/file.js';
import { stripTags, truncate } from '../../../../util/text.js';

import { getContentBlockItemValue, getContentBlockItemsByName, getContentBlockMixedItem, getContentBlockItemByName } from '../../CmsActions.js';
import { getLanguagePath } from '../../../Intl/IntlHelper.js';

import PredictiveLink from './PredictiveLink.js';
import Slider from '../../../../components/Content/Slider.js';
import HtmlComponent from '../../../../components/Text/HtmlComponent.js';

const WORDS_PER_MINUTE = 200;

function getPostImage(post) {
	const directImage = getContentBlockItemByName(post.blocks, 'image');
	if(directImage && directImage.path) {
		return directImage;
	}
	const contents = getContentBlockItemsByName(post.blocks, 'content');
	const contentWithImage = contents.find(content => (getContentBlockMixedItem(content, 'image') || {}).path);
	return getContentBlockMixedItem(contentWithImage, 'image');
}

function getReadingTime(post) {
	const text = getContentBlockItemsByName(post.blocks, 'content')
		.map(content => (getContentBlockMixedItem(content, 'text') || {}).text || '')
		.join(' ');
	const words = stripTags(text).split(/\s+/).filter(Boolean).length;
	// The `content` block is dropped on truncated (isPartial) posts, so it may be unavailable here;
	// return 0 in that case and let the caller hide the reading time rather than showing a wrong "1 min".
	return words ? Math.max(1, Math.round(words / WORDS_PER_MINUTE)) : 0;
}

function PostCard({ post, excerptSize = 80 }) {
	const contents = getContentBlockItemsByName(post.blocks, 'content');
	const image = getPostImage(post);
	const badge = (getContentBlockItemsByName(post.blocks, 'tags')[0] || {}).text;
	const excerpt = getContentBlockItemValue(post.blocks, 'excerpt')
		|| ((getContentBlockMixedItem(contents[0], 'text') || {}).text ? (getContentBlockMixedItem(contents[0], 'text') || {}).text : '');
	const readingTime = getReadingTime(post);

	return (
		<PredictiveLink tag="a" href={getLanguagePath(post.slug, post.language)} usePrefetch className="d-block h-100 text-reset text-decoration-none">
			<Card className="post-card h-100 border shadow-sm overflow-hidden">
				<div className="position-relative">
					{image ? (
						<img src={getFile(image.path)} alt={image.text || post.title} loading="lazy" className="w-100" style={{ height: '170px', objectFit: 'cover' }} />
					) : (
						<div className="w-100 bg-light" style={{ height: '170px' }} />
					)}
					{badge && (
						<span className="position-absolute top-0 start-0 m-2 badge bg-dark text-uppercase fw-semibold">{badge}</span>
					)}
				</div>
				<Card.Body className="d-flex flex-column gap-2">
					<h3 className="h6 fw-bold lh-sm mb-0">{post.title}</h3>
					<p className="text-muted small mb-0 flex-grow-1"><HtmlComponent text={excerpt} truncate={excerptSize} /></p>
					<div className="d-flex align-items-center justify-content-between mt-1">
						{readingTime > 0 ? (
							<span className="text-muted small">
								{'📖 '}
								<FormattedMessage id="postReadingTime" defaultMessage="{count} min read" values={{ count: readingTime }} />
							</span>
						) : <span />}
						<span className="fw-bold small text-danger">
							<FormattedMessage id="readArticle" defaultMessage="Read article" /> &rarr;
						</span>
					</div>
				</Card.Body>
			</Card>
		</PredictiveLink>
	);
}

PostCard.propTypes = {
	post: PropTypes.object.isRequired,
	excerptSize: PropTypes.number,
};

function PostCardGrid({ posts = [], appIsMobile = false, excerptSize = 80 }) {
	if(!posts.length) {
		return null;
	}

	if(appIsMobile) {
		return (
			<div className="d-flex flex-nowrap overflow-auto gap-3 pb-2">
				{posts.map(post => (
					<div key={post._id} className="flex-shrink-0" style={{ width: '230px' }}>
						<PostCard post={post} excerptSize={excerptSize} />
					</div>
				))}
			</div>
		);
	}

	return (
		<Slider
			interval={7000}
			disableControls={posts.length <= 3}
			slides={Array.from({ length: Math.ceil(posts.length / 3) }, (unused, slideIndex) => ({
				content: (
					<Row className="g-3 justify-content-center px-4 px-md-5">
						{posts.slice(slideIndex * 3, (slideIndex * 3) + 3).map(post => (
							<Col key={post._id} xs="12" md="4">
								<PostCard post={post} excerptSize={excerptSize} />
							</Col>
						))}
					</Row>
				),
			}))}
		/>
	);
}

function mapStateToProps(store) {
	return {
		appIsMobile: store.app.isMobile,
	};
}

PostCardGrid.propTypes = {
	posts: PropTypes.arrayOf(PropTypes.object),
	appIsMobile: PropTypes.bool,
	excerptSize: PropTypes.number,
};

export default connect(mapStateToProps)(PostCardGrid);
